import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, LayoutTemplate, Trash2, Eye, X, Pencil, Copy } from "lucide-react";

const DOC_TYPES = [
  { value: "invoice", label: "Invoices" },
  { value: "quote", label: "Quotes" },
  { value: "project_report", label: "Project reports" },
];

export default function PDFTemplates() {
  const [docType, setDocType] = useState("invoice");
  const [builtins, setBuiltins] = useState([]);
  const [customs, setCustoms] = useState([]);
  const [editor, setEditor] = useState(null); // { template_id?, name, html }
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const loadReqRef = useRef(0);

  const load = useCallback(async () => {
    // Bumping this invalidates any still-in-flight load, so switching doc
    // types quickly can't have a slower earlier type's response overwrite
    // the type currently selected.
    const reqId = ++loadReqRef.current;
    try {
      const [bRes, cRes] = await Promise.all([
        api.get(`/pdf-templates/builtin?doc_type=${docType}&include_html=true`),
        api.get(`/pdf-templates?doc_type=${docType}`),
      ]);
      if (reqId !== loadReqRef.current) return;
      setBuiltins(bRes.data || []);
      setCustoms(cRes.data || []);
    } catch {
      if (reqId === loadReqRef.current) toast.error("Failed to load templates");
    }
  }, [docType]);

  useEffect(() => { load(); }, [load]);

  const openNew = (startFrom) => {
    setEditor({ name: "", html: startFrom || builtins[0]?.html || "" });
  };

  const openEdit = (t) => {
    setEditor({ template_id: t.template_id, name: t.name, html: t.html });
  };

  const save = async () => {
    if (!editor.name.trim()) return toast.error("Name is required");
    if (!editor.html.trim()) return toast.error("Template HTML is required");
    setSaving(true);
    try {
      if (editor.template_id) {
        await api.put(`/pdf-templates/${editor.template_id}`, { name: editor.name, html: editor.html });
        toast.success("Template updated");
      } else {
        await api.post("/pdf-templates", { name: editor.name, doc_type: docType, html: editor.html });
        toast.success("Template created");
      }
      setEditor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/pdf-templates/${t.template_id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete template");
    }
  };

  const preview = async (html) => {
    setPreviewing(true);
    try {
      const res = await api.post("/pdf-templates/preview", { doc_type: docType, html }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      const detail = e?.response?.data instanceof Blob
        ? await e.response.data.text().then(t => { try { return JSON.parse(t).detail; } catch { return null; } }).catch(() => null)
        : e?.response?.data?.detail;
      toast.error(detail || "Preview failed — check your template for syntax errors");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <DarkPage><div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><LayoutTemplate className="w-5 h-5 text-[#7d8590]" /> PDF Templates</h1>
          <p className="text-sm text-[#7d8590] mt-1">Write your own HTML + CSS for invoices, quotes, and project reports. Jinja2 variables like <code className="text-white">{"{{ doc.client_name }}"}</code> are filled in at render time.</p>
        </div>
        <Button onClick={() => openNew()} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Template
        </Button>
      </div>

      <div className="flex gap-1 mb-6 bg-[#0d1117] border border-[#21262d] rounded-lg p-1 w-fit">
        {DOC_TYPES.map(dt => (
          <button key={dt.value} onClick={() => setDocType(dt.value)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${docType === dt.value ? "bg-[#1c2128] text-white" : "text-[#7d8590] hover:text-white"}`}>
            {dt.label}
          </button>
        ))}
      </div>

      <div className="mb-8">
        <h2 className="text-xs uppercase tracking-wide text-[#7d8590] mb-2">Built-in styles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {builtins.map(t => (
            <div key={t.template_id} className="bg-[#0d1117] border border-[#21262d] rounded-lg p-4 flex items-center justify-between">
              <span className="text-sm text-white">{t.name}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => preview(t.html)} disabled={previewing} className="p-1.5 text-[#7d8590] hover:text-white" title="Preview"><Eye className="w-3.5 h-3.5" /></button>
                <button onClick={() => openNew(t.html)} className="p-1.5 text-[#7d8590] hover:text-white" title="Use as starting point for a custom template"><Copy className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-xs uppercase tracking-wide text-[#7d8590] mb-2">Custom templates</h2>
        {customs.length === 0 ? (
          <div className="text-center py-10 text-[#7d8590] text-sm border border-dashed border-[#21262d] rounded-lg">
            No custom templates for this document type yet.
          </div>
        ) : (
          <div className="space-y-2">
            {customs.map(t => (
              <div key={t.template_id} className="bg-[#0d1117] border border-[#21262d] rounded-lg p-4 flex items-center justify-between">
                <span className="text-sm text-white">{t.name}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => preview(t.html)} disabled={previewing} className="p-1.5 text-[#7d8590] hover:text-white" title="Preview"><Eye className="w-3.5 h-3.5" /></button>
                  <button onClick={() => openEdit(t)} className="p-1.5 text-[#7d8590] hover:text-white" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(t)} className="p-1.5 text-[#7d8590] hover:text-rose-400" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-4xl h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-[#21262d]">
              <input value={editor.name} onChange={e => setEditor(ed => ({ ...ed, name: e.target.value }))}
                placeholder="Template name"
                className="bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-1.5 text-sm w-64" />
              <button onClick={() => setEditor(null)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <textarea
              value={editor.html}
              onChange={e => setEditor(ed => ({ ...ed, html: e.target.value }))}
              spellCheck={false}
              className="flex-1 bg-[#0a0d12] text-[#c9d1d9] font-mono text-xs p-4 resize-none outline-none"
            />
            <div className="flex items-center gap-2 p-4 border-t border-[#21262d]">
              <Button onClick={save} disabled={saving} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Saving…" : "Save template"}
              </Button>
              <Button onClick={() => preview(editor.html)} disabled={previewing} variant="outline" className="border-[#30363d] text-white">
                <Eye className="w-3.5 h-3.5 mr-1.5" /> {previewing ? "Rendering…" : "Preview"}
              </Button>
              <Button onClick={() => setEditor(null)} variant="outline" className="border-[#30363d] text-[#7d8590] ml-auto">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
